import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Search,
  TrendingUp,
  Sparkles,
  Clock,
  CheckCircle2,
} from "lucide-react";
import { usePublicClient } from "wagmi";

import MarketCard from "@/components/MarketCard";
import CategoryFilter from "@/components/CategoryFilter";
import { factoryAbi } from "@/contracts/factoryAbi";
import { factoryAddress } from "@/contracts/factoryAddress";
import { Market } from "@/types/market";

function detectCategory(question: string): string {
  const q = question.toLowerCase();

  if (q.includes("bitcoin") || q.includes("btc") || q.includes("ethereum") || q.includes("eth") || q.includes("crypto")) {
    return "crypto";
  }
  if (q.includes("arsenal") || q.includes("football") || q.includes("match") || q.includes("sports")) {
    return "sports-greece";
  }
  if (q.includes("cyprus") && (q.includes("election") || q.includes("president") || q.includes("parliament") || q.includes("politic"))) {
    return "cyprus-politics";
  }
  if (q.includes("greece") && (q.includes("election") || q.includes("prime minister") || q.includes("parliament") || q.includes("politic"))) {
    return "greece-politics";
  }
  if (q.includes("cyprus") && (q.includes("gdp") || q.includes("economy") || q.includes("inflation") || q.includes("rate"))) {
    return "cyprus-economy";
  }
  if (q.includes("greece") && (q.includes("gdp") || q.includes("economy") || q.includes("inflation") || q.includes("rate"))) {
    return "greece-economy";
  }
  if (q.includes("cyprus") && (q.includes("real estate") || q.includes("property") || q.includes("housing"))) {
    return "cyprus-real-estate";
  }
  if (q.includes("greece") && (q.includes("real estate") || q.includes("property") || q.includes("housing"))) {
    return "greece-real-estate";
  }

  return "global-markets";
}

function isExpired(deadlineIso: string) {
  return new Date(deadlineIso).getTime() <= Date.now();
}

type MarketStatusFilter = "open" | "resolved" | "all";

const MarketsPage = () => {
  const { t, i18n } = useTranslation();
  const publicClient = usePublicClient();

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState<MarketStatusFilter>("open");
  const [sort, setSort] = useState<"volume" | "newest" | "closing">("volume");
  const [markets, setMarkets] = useState<Market[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadMarkets() {
      try {
        setIsLoading(true);
        setLoadError("");

        if (!publicClient) return;

        const ids = (await (publicClient as any).readContract({
          address: factoryAddress,
          abi: factoryAbi,
          functionName: "getActiveMarketIds",
          args: [],
        })) as bigint[];

        const loadedMarkets: Market[] = [];

        for (const marketId of ids) {
          const market = await (publicClient as any).readContract({
            address: factoryAddress,
            abi: factoryAbi,
            functionName: "getMarket",
            args: [marketId],
          });

          const prices = await (publicClient as any).readContract({
            address: factoryAddress,
            abi: factoryAbi,
            functionName: "getPrices",
            args: [marketId],
          });

          const id = market[0].toString();
          const question = String(market[1]);
          const resolutionSource = String(market[2] ?? "");
          const deadlineUnix = Number(market[3]);
          const resolved = Boolean(market[5]);
          const yesPoolWei = BigInt(market[7].toString());
          const noPoolWei = BigInt(market[8].toString());
          const yesPriceBps = Number(prices[0]);
          const noPriceBps = Number(prices[1]);

          const totalVolume = Number(yesPoolWei + noPoolWei) / 1e18;
          const endDateIso = new Date(deadlineUnix * 1000).toISOString();
          const expired = isExpired(endDateIso);

          const status: "active" | "resolved" = resolved || expired ? "resolved" : "active";

          loadedMarkets.push({
            id,
            question_en: question,
            question_gr: question,
            description_en: resolutionSource ? `Resolution source: ${resolutionSource}` : "Open market.",
            description_gr: resolutionSource ? `Πηγή επίλυσης: ${resolutionSource}` : "Ανοιχτή αγορά.",
            category: detectCategory(question),
            end_date: endDateIso,
            resolution_source: resolutionSource || "Orion admin",
            status,
            yes_price: yesPriceBps / 10000,
            no_price: noPriceBps / 10000,
            volume: totalVolume,
            created_at: endDateIso,
          });
        }

        if (!cancelled) setMarkets(loadedMarkets);
      } catch (error: any) {
        console.error(error);
        if (!cancelled) {
          setLoadError(error?.shortMessage || error?.message || "Failed to load on-chain markets.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadMarkets();
    return () => {
      cancelled = true;
    };
  }, [publicClient]);

  const filtered = useMemo(() => {
    let m = [...markets];

    if (statusFilter === "open") m = m.filter((x) => x.status === "active");
    else if (statusFilter === "resolved") m = m.filter((x) => x.status === "resolved");

    if (category !== "all") m = m.filter((x) => x.category === category);

    if (search) {
      const q = search.toLowerCase();
      m = m.filter((x) =>
        (i18n.language === "en" ? x.question_en : x.question_gr).toLowerCase().includes(q)
      );
    }

    if (sort === "volume") m.sort((a, b) => b.volume - a.volume);
    else if (sort === "newest") m.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
    else m.sort((a, b) => +new Date(a.end_date) - +new Date(b.end_date));

    return m;
  }, [markets, search, category, sort, statusFilter, i18n.language]);

  const openMarketsCount = useMemo(() => markets.filter((m) => m.status === "active").length, [markets]);
  const resolvedMarketsCount = useMemo(() => markets.filter((m) => m.status === "resolved").length, [markets]);
  const totalLiquidity = useMemo(() => markets.reduce((sum, market) => sum + market.volume, 0), [markets]);

  const sorts = [
    { key: "volume" as const, label: t("markets.trending"), icon: TrendingUp },
    { key: "newest" as const, label: t("markets.new"), icon: Sparkles },
    { key: "closing" as const, label: t("markets.closing"), icon: Clock },
  ];

  const statusTabs = [
    { key: "open" as const, label: "Open", icon: TrendingUp },
    { key: "resolved" as const, label: "Resolved", icon: CheckCircle2 },
    { key: "all" as const, label: "All", icon: Sparkles },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 py-10">
      <div className="mb-10">
        <h1 className="text-[34px] font-bold tracking-tight text-foreground">
          {t("markets.title")}
        </h1>
        <p className="mt-2 max-w-2xl text-[15px] text-muted-foreground">
          {t("markets.subtitle")}
        </p>
      </div>

      <div className="mb-8 grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Open markets</div>
          <div className="mt-2 text-3xl font-semibold">{openMarketsCount}</div>
        </div>
        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Resolved markets</div>
          <div className="mt-2 text-3xl font-semibold">{resolvedMarketsCount}</div>
        </div>
        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Total liquidity</div>
          <div className="mt-2 text-3xl font-semibold">{totalLiquidity.toFixed(3)} POL</div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {statusTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`category-chip px-3.5 py-2 text-[12px] font-medium transition-colors ${
              statusFilter === tab.key ? "category-chip-active" : "hover:text-foreground"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <tab.icon className="h-3 w-3" />
              {tab.label}
            </span>
          </button>
        ))}
      </div>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t("markets.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border border-input bg-card py-3 pl-10 pr-4 text-[14px] focus:outline-none focus:ring-2 focus:ring-ring/30"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {sorts.map((s) => (
            <button
              key={s.key}
              onClick={() => setSort(s.key)}
              className={`category-chip px-3.5 py-2 text-[12px] font-medium transition-colors ${
                sort === s.key ? "category-chip-active" : "hover:text-foreground"
              }`}
            >
              <span className="flex items-center gap-1.5">
                <s.icon className="h-3 w-3" />
                {s.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <CategoryFilter selected={category} onSelect={setCategory} />
      </div>

      {loadError ? (
        <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {loadError}
        </div>
      ) : null}

      <div className="mb-5 text-[12px] font-medium text-muted-foreground">
        {filtered.length} {i18n.language === "en" ? "markets" : "αγορές"}
      </div>

      {isLoading ? (
        <div className="rounded-2xl border bg-card p-10 text-center text-muted-foreground shadow-sm">
          Loading on-chain markets...
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border bg-card p-10 text-center text-muted-foreground shadow-sm">
          {statusFilter === "open"
            ? "No open markets."
            : statusFilter === "resolved"
            ? "No resolved markets yet."
            : t("markets.noMarkets")}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((market) => (
            <MarketCard key={market.id} market={market} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketsPage;